import React, { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { formatTableLabel } from "./dineInBoardBuilder";
import { buildTableQrUrl, buildNineQrImageSettings } from "./dineInQrHelper";

/** QR resolution on the tent — see the comment at the render site. */
const TENT_QR_SIZE = 256;

interface DineInTableTentProps {
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  tableNumber: number;
}

/**
 * One panel's worth of content. Rendered twice per tent — see the component
 * below for why one of the two is upside down.
 */
const TentFace: React.FC<
  DineInTableTentProps & { onLogoError: () => void }
> = ({ businessId, businessName, businessLogoUrl, tableNumber, onLogoError }) => (
  <>
    {/* Safe to draw here, unlike in the PNG export: this is a plain <img> in
        the print document, so cross-origin rules never come into it. */}
    {businessLogoUrl && (
      <img
        className="dine-in-tent__logo"
        src={businessLogoUrl}
        alt=""
        aria-hidden="true"
        onError={onLogoError}
      />
    )}
    <span className="dine-in-tent__business">{businessName}</span>
    <span className="dine-in-tent__table">{formatTableLabel(tableNumber)}</span>
    {/* Rendered at 256px and scaled down by CSS to ~34mm. A canvas sized to
        its printed dimensions would come out at screen density and look soft;
        oversampling costs nothing and prints crisp. */}
    <QRCodeCanvas
      value={buildTableQrUrl(businessId, tableNumber)}
      size={TENT_QR_SIZE}
      level="H"
      includeMargin={false}
      imageSettings={buildNineQrImageSettings(TENT_QR_SIZE)}
      className="dine-in-tent__qr"
    />
    <span className="dine-in-tent__hint">Scan to order from your phone</span>
  </>
);

/**
 * A physical table tent: one card, folded in half across the middle, standing
 * on a table so it reads from both sides.
 *
 * The top panel is rotated 180°, which is the whole trick. Once the card is
 * folded along the centre line, that panel becomes the far face — and without
 * the rotation it would be upside down to whoever is sitting on that side.
 * Printing two identical upright panels produces something that only works if
 * you look at it from exactly one direction.
 */
const DineInTableTent: React.FC<DineInTableTentProps> = (props) => {
  // Held here rather than inside TentFace so BOTH panels agree. Per-face state
  // could leave one side of a folded card showing a broken-image glyph and the
  // other side clean — on something being printed and put on a table.
  const [logoFailed, setLogoFailed] = useState(false);
  const faceProps = {
    ...props,
    businessLogoUrl: logoFailed ? undefined : props.businessLogoUrl,
    onLogoError: () => setLogoFailed(true),
  };

  return (
    <div className="dine-in-tent">
      <div className="dine-in-tent__panel dine-in-tent__panel--flipped">
        <TentFace {...faceProps} />
      </div>
      <div className="dine-in-tent__fold">
        <span>fold</span>
      </div>
      <div className="dine-in-tent__panel">
        <TentFace {...faceProps} />
      </div>
    </div>
  );
};

export default DineInTableTent;
